// src/lib/supabase.ts
// Single Supabase client used everywhere in the app

import { createClient } from '@supabase/supabase-js';

const supabaseUrl  = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnon = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnon) {
  // Fail loud at boot so the deploy is obviously broken instead of silently
  // returning cryptic "fetch failed" errors at every query later.
  throw new Error(
    'Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY. ' +
    'Copy .env.example to .env.local and fill in your Supabase project credentials.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnon, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: { eventsPerSecond: 20 },
  },
});

// ── Typed DB helpers ─────────────────────────────────────────────────────────
// These replace the old Express API calls with direct Supabase queries

export const db = {
  // Players
  players: () => supabase.from('players'),
  playerStats: () => supabase.from('player_career_stats'),

  // Clubs
  clubs: () => supabase.from('clubs'),
  clubMembers: () => supabase.from('club_members'),
  clubStats: () => supabase.from('club_stats'),

  // Matches
  matches: () => supabase.from('matches'),
  innings: () => supabase.from('innings'),
  deliveries: () => supabase.from('deliveries'),
  battingCards: () => supabase.from('batting_scorecards'),
  bowlingCards: () => supabase.from('bowling_scorecards'),
  squads: () => supabase.from('match_squads'),

  // Tournaments
  tournaments: () => supabase.from('tournaments'),
  registrations: () => supabase.from('tournament_registrations'),
  groups: () => supabase.from('tournament_groups'),
  standings: () => supabase.from('tournament_group_clubs'),
  authorities: () => supabase.from('authorities'),

  // Marketplace
  interests: () => supabase.from('marketplace_interests'),
  shortlists: () => supabase.from('club_shortlists'),

  // Availability
  availability: () => supabase.from('player_availability'),

  // Practice
  practiceSessions: () => supabase.from('practice_sessions'),
  practiceAttendance: () => supabase.from('practice_attendance'),

  // Key Contacts & Gallery
  keyContacts: () => supabase.from('key_contacts'),
  photoGallery: () => supabase.from('photo_gallery'),

  // Transfers
  transfers: () => supabase.from('player_transfers'),

  // Disputes
  disputes: () => supabase.from('disputes'),

  // Umpires & Venues
  umpires: () => supabase.from('umpires'),
  venues: () => supabase.from('venues'),
  venueSlots: () => supabase.from('venue_slots'),

  // Auction
  auctionRooms: () => supabase.from('auction_rooms'),
  auctionPlayers: () => supabase.from('auction_players'),
  auctionBids: () => supabase.from('auction_bids'),
  auctionTeams: () => supabase.from('auction_teams'),

  // Junior
  juniors: () => supabase.from('junior_profiles'),

  // Payments
  payments: () => supabase.from('payments'),

  // Feed
  feed: () => supabase.from('feed_posts'),
  feedLikes: () => supabase.from('feed_likes'),

  // Notifications
  notifications: () => supabase.from('notifications'),
};

// ── Realtime channel helpers ───────────────────────────────────────────────
export const realtime = {
  // Subscribe to live scoring for a match
  matchScoring: (matchId: string, onDelivery: (payload: any) => void) => {
    return supabase
      .channel(`match-scoring-${matchId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'deliveries',
        filter: `innings_id=eq.${matchId}`,
      }, onDelivery)
      .subscribe();
  },

  // Subscribe to innings score changes
  inningsScore: (inningsId: string, onUpdate: (payload: any) => void) => {
    return supabase
      .channel(`innings-${inningsId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'innings',
        filter: `id=eq.${inningsId}`,
      }, onUpdate)
      .subscribe();
  },

  // Live auction bids
  auctionBids: (auctionId: string, onBid: (payload: any) => void) => {
    return supabase
      .channel(`auction-${auctionId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'auction_bids',
        filter: `auction_room_id=eq.${auctionId}`,
      }, onBid)
      .subscribe();
  },

  // User notifications
  userNotifications: (userId: string, onNotif: (payload: any) => void) => {
    return supabase
      .channel(`notif-${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, onNotif)
      .subscribe();
  },
};

export default supabase;
